"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import type { CreateDraft, Product, Project, VideoStyle } from "@easyshot/shared";
import { PLATFORM_PRESETS, STYLE_OPTIONS } from "@easyshot/shared";
import { api } from "@/lib/api";
import { Badge, Card, LinkButton, Spinner } from "./ui";

function styleLabel(style: string): string {
  if (style === "minimax_h3_product_video") return "MiniMax H3 Product Video";
  if (style === "infinitetalk_avatar") return "InfiniteTalk Avatar Video";
  return STYLE_OPTIONS.find((option) => option.id === style)?.label ?? style;
}

export function ProjectList() {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [drafts, setDrafts] = useState<CreateDraft[] | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const [projectResult, draftResult] = await Promise.allSettled([
        api.listProjects(),
        api.listDrafts(),
      ]);
      if (!alive) return;
      setProjects(projectResult.status === "fulfilled" ? projectResult.value : []);
      setDrafts(draftResult.status === "fulfilled" ? draftResult.value : []);
    };
    void load();
    // 有任务在跑时列表上的进度也要动
    const t = setInterval(load, 3000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:py-10">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
          <p className="mt-2 text-sm text-muted">
            Drafts, renders in progress, failed attempts, and completed videos — all in one place.
          </p>
        </div>
        <LinkButton href="/studio">+ New</LinkButton>
      </div>

      {(projects === null || drafts === null) && (
        <div className="grid place-items-center py-20 text-muted">
          <Spinner className="size-6" />
        </div>
      )}

      {projects?.length === 0 && drafts?.length === 0 && (
        <Card className="px-6 py-20 text-center">
          <div className="text-4xl">🎬</div>
          <h2 className="mt-4 text-lg font-semibold">No projects yet</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm text-muted">
            Paste a product link and see your first script and storyboard in 30
            seconds.
          </p>
          <LinkButton href="/studio" className="mt-6">
            Create your first project
          </LinkButton>
        </Card>
      )}

      {drafts && drafts.length > 0 && (
        <section className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Drafts</h2>
            <span className="text-xs text-muted">{drafts.length} saved</span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {drafts.map((draft) => {
              const payload = draft.payload as {
                product?: Product | null;
                style?: VideoStyle;
              };
              const image = payload.product?.images[0];
              return (
                <Link
                  key={draft.id}
                  href={`/studio?draft=${encodeURIComponent(draft.id)}`}
                >
                  <Card className="flex h-full gap-4 border-[var(--accent)]/30 p-4 transition hover:border-[var(--accent)]/60 hover:bg-white/[0.06]">
                    {image ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={image}
                        alt=""
                        className="size-20 shrink-0 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="grid size-20 shrink-0 place-items-center rounded-xl bg-white/[0.05] text-2xl">
                        📝
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="truncate text-sm font-medium">{draft.name}</div>
                        <Badge tone="accent">Draft</Badge>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {payload.style && (
                          <Badge>
                            {styleLabel(payload.style)}
                          </Badge>
                        )}
                        <Badge>Step {Math.min(4, draft.step + 1)}/4</Badge>
                      </div>
                      <div className="mt-3 text-[12px] text-muted">
                        Saved {new Date(draft.updatedAt).toLocaleString()}
                      </div>
                      <div className="mt-2 text-[12px] font-medium text-purple-300">
                        Continue Create →
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {projects && projects.length > 0 && (
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Generated projects</h2>
          <span className="text-xs text-muted">{projects.length} projects</span>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {projects?.map((p) => {
          const done = p.jobs.filter((j) => j.status === "succeeded").length;
          const failed = p.jobs.filter((j) => j.status === "failed").length;
          const running = p.jobs.filter(
            (j) => j.status !== "succeeded" && j.status !== "failed",
          ).length;
          return (
            <Link key={p.id} href={`/studio/projects/${p.id}`}>
              <Card className="flex h-full gap-4 p-4 transition hover:border-[var(--border-strong)] hover:bg-white/[0.06]">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={p.product.images[0]}
                  alt=""
                  className="size-20 shrink-0 rounded-xl object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{p.name}</div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <Badge>
                      {styleLabel(p.style)}
                    </Badge>
                    {p.platforms.slice(0, 2).map((pl) => (
                      <Badge key={pl}>
                        {PLATFORM_PRESETS.find((x) => x.id === pl)?.label}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-[12px] text-muted">
                    <span>{p.jobs.length} videos</span>
                    {running > 0 && (
                      <span className="flex items-center gap-1.5 text-[var(--warning)]">
                        <Spinner className="size-3 border-[var(--warning)]/30 border-t-[var(--warning)]" />
                        {running} rendering
                      </span>
                    )}
                    {running === 0 && done > 0 && (
                      <span className="text-[var(--success)]">
                        {done} ready
                      </span>
                    )}
                    {failed > 0 && (
                      <span className="text-[var(--danger)]">
                        {failed} failed
                      </span>
                    )}
                    {p.jobs.length === 0 && (
                      <span className="text-[var(--warning)]">Not generated</span>
                    )}
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
